/// <amd-module name="Test/TestResultDetails/Actions/TestResultDetailsActionCreator" />
import { getResultViewModelFromTestResult, getRunViewModelFromTestRun } from "Test/common-converters/TestResultConverter";
import { IViewContextData, ViewContext } from "Test/common-util/Common";
import { TestCaseResult, TestRun } from "Test/common-util/Contracts/Test";
import { ITestDetailsPanePayload, ITestResultTreeData, TreeNodeType } from "Test/common-util/Contracts/ViewContracts";
import {
    TestResultDetailsActionHub,
    TestResultDetailsPanePivot,
} from "Test/TestResultDetails/Actions/TestResultDetailsActionHub";
import { TestResultSource } from "Test/TestResultDetails/Source/TestResultSource";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export class TestResultDetailsActionCreator {
    private _source: TestResultSource;

    constructor(private _resultDetailsActionHub: TestResultDetailsActionHub,
        private _pageContext: IVssPageContext,
        private _viewContextData: IViewContextData) {
        this._source = TestResultSource.getInstance();
    }

    public pivotSelected(pivot: string) {
        this._resultDetailsActionHub.changeDetailsPivot.invoke(pivot);
    }

    public saveHeaderState(currentState: boolean): void {
        this._resultDetailsActionHub.captureHeaderState.invoke(currentState);
    }

    public initializeWithFullScreen(runId: number, resultId: number, paneView: string | undefined): void {
        if (runId && resultId && resultId != 0) {

            /* When full screen details of result viewed, we need to include subResults with results because when we load test tab for the first time 
            for a result in fullscreen we aren't aware of about subresults of that result at this point. This is required because the tree works 
            independently and has its own way to get results and respective (existing) subResults, it may break if we assume that subReults are already present.
            Hence true passed for getSubResults. */
            this._source.getSelectedTestCaseResult(this._pageContext, runId, resultId, true).then((testResultDetails: TestCaseResult) => {
                let testResultTreeData = getResultViewModelFromTestResult(testResultDetails, this._viewContextData, "-1");
                this._resultDetailsActionHub.updateDetailsPane.invoke(
                    {
                        storeTestCaseResultTreeData: testResultTreeData,
                        detailedTestResult: testResultDetails,
                        openPane: paneView
                    } as ITestDetailsPanePayload
                );
            }).catch((reason: any) => {
                this._resultDetailsActionHub.onError.invoke(reason.toString());
            });
        } else if (runId && runId > 0) {
            let includeTags = isFeatureFlagEnabled(this._pageContext, this._tagsForTestRunFF, false)
            this._source.getTestRunById(this._pageContext, runId, includeTags).then((testRun: TestRun) => {
                let testResultTreeData = getRunViewModelFromTestRun(testRun, this._viewContextData);
                this._resultDetailsActionHub.updateDetailsPane.invoke(
                    {
                        testRun: testRun,
                        storeTestCaseResultTreeData: testResultTreeData,
                        openPane: paneView
                    } as ITestDetailsPanePayload
                );
            }).catch((reason: any) => {
                this._resultDetailsActionHub.onError.invoke(reason.toString());
           });
        }
    }

    public initialize(testResultTreeData: ITestResultTreeData, paneView: string = TestResultDetailsPanePivot.Debug): Promise<void> {
        if (testResultTreeData.isTestCaseRow && testResultTreeData.resultId) {
            // show spinner while fetching TestCase result details from server
            this._resultDetailsActionHub.cleanDetailsPane.invoke(undefined);

            // The results cache showed in tree is different. First time when sub result is clicked, we have to make sub results call. Bug #1585006
            const subResultExits = testResultTreeData.nodeType === TreeNodeType.group || !!testResultTreeData.isSubResultRow;
            return this._source.getSelectedTestCaseResult(this._pageContext, testResultTreeData.runId, testResultTreeData.resultId, subResultExits).then((testResultDetails: TestCaseResult) => {
                if (testResultDetails) {
                    if (testResultDetails.failingSince && this._viewContextData) {
                        const failingSince = testResultDetails.failingSince;
                        if (this._viewContextData.viewContext === ViewContext.Build) {
                            testResultTreeData.failingContextId = failingSince.build ? failingSince.build.id : 0;
                            testResultTreeData.failingContextName = failingSince.build ? failingSince.build.number : "";
                        } else if (this._viewContextData.viewContext === ViewContext.Release) {
                            testResultTreeData.failingContextId = failingSince.release ? failingSince.release.id : 0;
                            testResultTreeData.failingContextName = failingSince.release ? failingSince.release.name : "";
                        }
                    }
                    testResultTreeData.owner = (testResultDetails.owner) ? testResultDetails.owner.displayName : "";
                }
                this._resultDetailsActionHub.updateDetailsPane.invoke(
                    {
                        storeTestCaseResultTreeData: testResultTreeData,
                        detailedTestResult: testResultDetails,
                        openPane: paneView
                    } as ITestDetailsPanePayload
                );
            }).then(undefined, error => {
                this._resultDetailsActionHub.onError.invoke(error.toString());
            });
        } else if (testResultTreeData.runId > 0) {
            this._resultDetailsActionHub.cleanDetailsPane.invoke(undefined);
            let includeTags = isFeatureFlagEnabled(this._pageContext, this._tagsForTestRunFF, false)
            return this._source.getTestRunById(this._pageContext, testResultTreeData.runId, includeTags).then((testRun: TestRun) => {
                this._resultDetailsActionHub.updateDetailsPane.invoke(
                    {
                        testRun: testRun,
                        storeTestCaseResultTreeData: testResultTreeData
                    }
                );
            }).then(undefined, error => {
                this._resultDetailsActionHub.onError.invoke(error.toString());
            });
        }
        return Promise.resolve();
    }

    private _tagsForTestRunFF: string = "TestManagement.Server.TagsInTestRun";
}
